import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  PromptInput,
  ModelSelect,
  SamplerSettings,
  DimensionsInput,
  LoraList,
  GenerateButton,
  PAGFreeUPanel,
  UpscaleImageInput,
} from '@/features/generation/components'
import { ProgressPanel } from '@/features/generation/components/ProgressPanel'
import { useGenerationFormStore } from '@/features/generation/store'
import { useSettings } from '@/features/settings/hooks'
import { THEME } from '@/features/settings/types'
import type { Theme } from '@/features/settings/types'

export function GeneratePage() {
  const { t } = useTranslation()
  const activeGenerationId = useGenerationFormStore((s) => s.activeGenerationId)
  const applyDefaults = useGenerationFormStore((s) => s.applyDefaults)
  const { data: settings } = useSettings()
  const defaultsApplied = useRef(false)

  useEffect(() => {
    if (settings && !defaultsApplied.current) {
      defaultsApplied.current = true
      // Apply generation defaults (guard against missing nested object)
      const gd = settings.generation_defaults
      if (gd) {
        applyDefaults({
          checkpoint: gd.default_checkpoint,
          negativePrompt: gd.default_negative_prompt,
          samplerName: gd.default_sampler,
          scheduler: gd.default_scheduler,
          steps: gd.default_steps,
          cfgScale: gd.default_cfg_scale,
          width: gd.default_width,
          height: gd.default_height,
          pagEnabled: gd.default_pag?.enabled ?? false,
          pagScale: gd.default_pag?.scale ?? 3.0,
          freeuEnabled: gd.default_freeu?.enabled ?? false,
          freeuB1: gd.default_freeu?.b1 ?? 1.1,
          freeuB2: gd.default_freeu?.b2 ?? 1.2,
          freeuS1: gd.default_freeu?.s1 ?? 0.9,
          freeuS2: gd.default_freeu?.s2 ?? 0.2,
        })
      }
      // Apply saved theme
      const theme = (settings.theme ?? THEME.SYSTEM) as Theme
      if (theme !== THEME.SYSTEM) {
        document.documentElement.setAttribute('data-theme', theme)
      }
    }
  }, [settings, applyDefaults])

  return (
    <div className="flex h-dvh flex-col pb-14 bg-tg-bg">
      <header
        className="shrink-0 px-4 py-3 bg-tg-bg"
        style={{
          borderBottom: '1px solid color-mix(in srgb, var(--tg-theme-hint-color) 10%, transparent)',
          boxShadow: '0 1px 3px 0 color-mix(in srgb, var(--tg-theme-hint-color) 8%, transparent)',
        }}
      >
        <h1 className="text-lg font-semibold text-tg-text">
          {t('generate.title')}
        </h1>
      </header>

      <main className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-4">
          {activeGenerationId !== null ? (
            <ProgressPanel generationId={activeGenerationId} />
          ) : (
            <>
              {/* Model section */}
              <section className="flex flex-col gap-3">
                <ModelSelect />
              </section>

              {/* Prompt section */}
              <section className="flex flex-col gap-3">
                <PromptInput />
              </section>

              {/* Dimensions section */}
              <section className="flex flex-col gap-3">
                <DimensionsInput />
              </section>

              {/* Sampler section */}
              <section className="flex flex-col gap-3">
                <SamplerSettings />
              </section>

              {/* LoRA section */}
              <section className="flex flex-col gap-3">
                <LoraList />
              </section>

              {/* PAG & FreeU section */}
              <section className="flex flex-col gap-3">
                <PAGFreeUPanel />
              </section>

              {/* Upscale section */}
              <section className="flex flex-col gap-3">
                <UpscaleImageInput />
              </section>
            </>
          )}
        </div>
      </main>

      <footer
        className="shrink-0 p-4 bg-tg-bg"
        style={{
          borderTop: '1px solid color-mix(in srgb, var(--tg-theme-hint-color) 10%, transparent)',
          boxShadow: '0 -1px 3px 0 color-mix(in srgb, var(--tg-theme-hint-color) 8%, transparent)',
        }}
      >
        <GenerateButton />
      </footer>
    </div>
  )
}
